import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Box,
  Typography,
  InputAdornment,
} from '@mui/material';
import { posterUrl, searchShows, TmdbSearchResult } from '../tmdb';

interface AddTVShowModalProps {
  show: boolean;
  onHide: () => void;
  onAddShow: (result: TmdbSearchResult) => Promise<void>;
}

const yearOf = (result: TmdbSearchResult) =>
  result.first_air_date ? result.first_air_date.split('-')[0] : '';

const AddTVShowModal = ({ show, onHide, onAddShow }: AddTVShowModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<TmdbSearchResult[]>([]);
  const [selected, setSelected] = useState<TmdbSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Reset when modal opens */
  useEffect(() => {
    if (show) {
      setSearchTerm('');
      setSearchResults([]);
      setSelected(null);
      setError(null);
    }
  }, [show]);

  /* Debounced live search */
  useEffect(() => {
    const trimmed = searchTerm.trim();

    if (!trimmed) {
      setSearchResults([]);
      setError(null);
      return;
    }

    const handler = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        setSearchResults(await searchShows(trimmed));
      } catch {
        setError('Failed to fetch TV show data.');
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleAdd = async () => {
    if (!selected) return;
    setAdding(true);
    try {
      await onAddShow(selected);
    } finally {
      setAdding(false);
    }
    onHide();
  };

  return (
    <Dialog open={show} onClose={onHide} maxWidth="md" fullWidth>
      <DialogTitle>Add TV Show</DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          autoFocus
          label="Search for a TV show"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {selected ? (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Selected Show
            </Typography>

            <Box
              component="img"
              src={posterUrl(selected.poster_path)}
              alt={selected.name}
              sx={{ width: 150, borderRadius: 1, mb: 2 }}
            />

            <Typography variant="body1">
              {selected.name}
              {yearOf(selected) && <> ({yearOf(selected)})</>}
            </Typography>

            <Button
              variant="text"
              onClick={() => setSelected(null)}
              sx={{ mt: 1 }}
            >
              Choose a different show
            </Button>
          </Box>
        ) : (
          searchResults.length > 0 && (
            <Grid
              container
              spacing={2}
              sx={{ maxHeight: 400, overflowY: 'auto', mt: 2 }}
            >
              {searchResults.map((result) => (
                <Grid key={result.id} size={{ xs: 6, sm: 4, md: 3 }}>
                  <Box
                    component="img"
                    src={posterUrl(result.poster_path)}
                    alt={result.name}
                    onClick={() => setSelected(result)}
                    sx={{
                      width: '100%',
                      aspectRatio: '2 / 3',
                      objectFit: 'cover',
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 },
                    }}
                  />
                  <Typography variant="body2" align="center">
                    {result.name}
                    {yearOf(result) && ` (${yearOf(result)})`}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onHide}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!selected || adding}
        >
          {adding ? 'Adding…' : 'Add Show'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTVShowModal;
